import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CircularProgress, Container, Typography } from '@mui/material';
import { Group as GroupIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contextApi, InvitationPreview } from '../services/contextApi';
import { useFinancialContexts } from '../contexts/ContextsContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout/Layout';
import { tokens } from '../theme/tokens';

type FailureKind = 'invalid' | 'expired' | 'used' | 'notForYou' | 'alreadyMember' | 'other';

/** The API's reasons for refusing an invitation, as the person reads them. */
const failureKind = (message: string | undefined, status: number | undefined): FailureKind => {
  if (message?.includes('already a member')) return 'alreadyMember';
  if (status === 404 || message?.includes('Invalid invitation token')) return 'invalid';
  if (message?.includes('expired')) return 'expired';
  if (message?.includes('already been processed')) return 'used';
  if (message?.includes('not for you')) return 'notForYou';
  return 'other';
};

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ py: 6, px: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
    <Card
      variant="outlined"
      sx={{ p: { xs: 3, sm: 4 }, maxWidth: 480, width: '100%', textAlign: 'center', borderRadius: '20px', borderColor: tokens.line }}
    >
      <GroupIcon sx={{ fontSize: 44, color: tokens.accent }} />
      {children}
    </Card>
  </Box>
);

/**
 * Signed in: the invitation is taken up on arrival, as the email's button
 * promised, unless it is an invitation to sign up for another email (the
 * family laptop, an account kept from before): then it asks first, with the
 * way to switch accounts.
 */
const AcceptInvitation: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation('contexts');
  const navigate = useNavigate();
  const { state: auth, logout } = useAuth();
  const { reload, selectContext } = useFinancialContexts();
  const me = auth.user;

  const [status, setStatus] = useState<'checking' | 'confirm' | 'accepting' | 'accepted' | 'failed'>('checking');
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [failure, setFailure] = useState<{ kind: FailureKind; message?: string } | null>(null);
  const [contextId, setContextId] = useState<string | null>(null);

  const accept = useCallback(async () => {
    setStatus('accepting');
    setFailure(null);
    try {
      const member = await contextApi.acceptInvitation(token);
      setContextId(member.contextId);
      setStatus('accepted');
      await reload();
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message;
      setFailure({ kind: failureKind(message, err?.response?.status), message });
      setStatus('failed');
    }
  }, [token, reload]);

  // Once: a second accept of the same link would only report it as used.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      let found: InvitationPreview | null = null;
      try {
        found = await contextApi.previewInvitation(token);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setFailure({ kind: 'invalid' });
          setStatus('failed');
          return;
        }
        // Could not look first: accepting says what is wrong, if anything.
      }
      setPreview(found);
      if (found?.expired) {
        setFailure({ kind: 'expired' });
        setStatus('failed');
        return;
      }
      if (found?.kind === 'signup' && found.email !== me?.email?.toLowerCase()) {
        setStatus('confirm');
        return;
      }
      accept();
    })();
  }, [token, accept, me?.email]);

  const openContext = () => {
    if (contextId) selectContext(contextId);
    navigate('/dashboard');
  };

  const switchAccount = (
    // Signing out shows the link as someone signed out sees it: sign in with the right account.
    <Button onClick={() => logout()}>{t('invitation.switchAccount')}</Button>
  );

  return (
    <Frame>
      {(status === 'checking' || status === 'accepting') && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {status === 'checking' ? t('invitation.loading') : t('invitation.accepting')}
          </Typography>
          <CircularProgress sx={{ mt: 3 }} />
        </>
      )}

      {status === 'confirm' && preview && (
        <>
          <Typography component="h1" variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
            {preview.inviterName
              ? t('invitation.title', { inviter: preview.inviterName, name: preview.contextName })
              : t('invitation.titleNoInviter', { name: preview.contextName })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('invitation.confirmAs', { who: me?.email || me?.firstName || '' })}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="contained" size="large" onClick={accept}>
              {t('invitation.join')}
            </Button>
            {switchAccount}
          </Box>
        </>
      )}

      {status === 'accepted' && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {t('invitation.accepted')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('invitation.acceptedHint')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={openContext}>
              {t('invitation.openContext')}
            </Button>
            <Button onClick={() => navigate('/contexts')}>{t('invitation.seeAll')}</Button>
          </Box>
        </>
      )}

      {status === 'failed' && failure && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {failure.kind === 'alreadyMember' ? t('invitation.alreadyMemberTitle') : t('invitation.failed')}
          </Typography>
          <Alert severity={failure.kind === 'alreadyMember' ? 'info' : 'error'} sx={{ my: 2, textAlign: 'left' }}>
            {failure.kind === 'other' ? failure.message : t(`invitation.errors.${failure.kind}`)}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {failure.kind === 'other' && (
              <Button variant="contained" onClick={accept}>
                {t('invitation.retry')}
              </Button>
            )}
            <Button onClick={() => navigate('/contexts')}>{t('invitation.seeAll')}</Button>
            {(failure.kind === 'notForYou' || failure.kind === 'alreadyMember') && switchAccount}
          </Box>
        </>
      )}
    </Frame>
  );
};

/**
 * Signed out: what the invitation is for, and the way in. Someone with no
 * account creates one (the invited email filled in) and comes back here,
 * signed in, to take it up; someone with an account signs in.
 */
const InvitationWelcome: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation('contexts');
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setPreview(await contextApi.previewInvitation(token));
      setState('ready');
    } catch (err: any) {
      setState(err?.response?.status === 404 ? 'notFound' : 'error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const back = `/invitations/${token}`;
  const toLogin = (params: Record<string, string>) =>
    navigate(`/login?${new URLSearchParams({ ...params, next: back }).toString()}`);

  if (state === 'loading') {
    return (
      <Frame>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {t('invitation.loading')}
        </Typography>
        <CircularProgress sx={{ mt: 3 }} />
      </Frame>
    );
  }

  if (state !== 'ready' || !preview) {
    return (
      <Frame>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {state === 'notFound' ? t('invitation.notFoundTitle') : t('invitation.loadFailed')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {state === 'notFound' ? t('invitation.notFoundHint') : t('invitation.loadFailedHint')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {state === 'error' && (
            <Button variant="contained" onClick={load}>
              {t('invitation.retry')}
            </Button>
          )}
          <Button onClick={() => navigate('/')}>{t('invitation.goHome')}</Button>
        </Box>
      </Frame>
    );
  }

  const inviter = preview.inviterName;
  if (preview.expired) {
    return (
      <Frame>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {t('invitation.expiredTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {inviter ? t('invitation.expiredHint', { inviter }) : t('invitation.expiredHintNoInviter')}
        </Typography>
        <Button onClick={() => navigate('/')}>{t('invitation.goHome')}</Button>
      </Frame>
    );
  }

  const email = preview.email ?? '';
  return (
    <Frame>
      <Typography component="h1" variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
        {inviter
          ? t('invitation.title', { inviter, name: preview.contextName })
          : t('invitation.titleNoInviter', { name: preview.contextName })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        {t('invitation.lead')} {preview.kind === 'signup' ? t('invitation.signupHint') : t('invitation.signInHint')}
      </Typography>
      {preview.kind === 'signup' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button variant="contained" size="large" onClick={() => toLogin({ tab: 'register', email })}>
            {t('invitation.createAccount')}
          </Button>
          <Button onClick={() => toLogin({})}>{t('invitation.haveAccount')}</Button>
        </Box>
      ) : (
        <Button variant="contained" size="large" fullWidth onClick={() => toLogin(email ? { email } : {})}>
          {t('invitation.signIn')}
        </Button>
      )}
    </Frame>
  );
};

/**
 * Where an invitation link lands, signed in or out: the email's button, or
 * the link someone shared by hand.
 */
const InvitationPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { state } = useAuth();

  if (state.isLoading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return state.isAuthenticated ? (
    <Layout>
      <AcceptInvitation token={token} />
    </Layout>
  ) : (
    <Container maxWidth="sm" disableGutters sx={{ minHeight: '85vh', display: 'flex', alignItems: 'center' }}>
      <InvitationWelcome token={token} />
    </Container>
  );
};

export default InvitationPage;
